import React, { useRef } from "react";
import "./termsAndConditions.scss";
import { BasicIcon, Tooltip, ToolTipPosition, Table, Tag, Button } from "@pickit/pickit-components";
import { useTranslation } from "react-i18next";
import "./i18n";
import { ISO8601toDDMMYYYHHMM } from "utils/date";
import PdfController from "component/pdfController/PdfController";
import { PdfViewer } from "component/pdf-viewer/PdfViewer";
import { TermsAndConditionsProps } from "./types";

export const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  testId = "terms-and-conditions",
  currentFile,
  fileStatus,
  termsVersions,
  loading,
  fileHandler,
  goToPreviousFile,
  downloadCurrentFile,
  downloadVersion,
  viewVersion,
  onUpdate,
  onDeleteFile,
  onFileTypeError,
}): JSX.Element => {
  const { t } = useTranslation();
  const pdfControllerRef = useRef<any>();

  const tableHeaders = [
    { label: t("termsAndConditions:version"), key: "version", textConfig: {
      formatter: (value: string) =>
        `V${value}`,
      weight: 700
    }, col: 3, testId: 'version' },
    { label: t("termsAndConditions:estado"), key: "estado", col: 4, testId: 'state' },
    { label: t("termsAndConditions:uploadDate"), key: "uploadDate", col: 5, testId: 'uploadDate' },
    { label: t("termsAndConditions:uploadedBy"), key: "uploadedBy.email", testId: 'uploadedBy' },
    { label: t("termsAndConditions:acciones"), key: "actions", col: 3, testId: 'actions' },
  ];

  const formatTableData = () => {
    if (!termsVersions || !Array.isArray(termsVersions)) {
      return [];
    }
    
    return termsVersions.map((version, index) => {
      return {
        version: version.version || "N/A",
        estado: index === 0 ? (
          <Tag
            status="success"
            label={t("termsAndConditions:activa")}
          />
        ) : null,
        uploadDate: version.uploadedAt 
          ? ISO8601toDDMMYYYHHMM(version.uploadedAt)
          : "N/A",
        uploadedBy: {
          email: version.uploadedBy?.email || "N/A",
        },
        actions: (
          <div className="actions-container">
            <Tooltip message={t("termsAndConditions:ver")} position={ToolTipPosition.top}>
              <div data-testId={`${testId}-view-version-${version.version}`}>
                <BasicIcon 
                  clickable={true} 
                  onClick={() => viewVersion(version.url || "")}
                  name="eye" 
                  size="small" 
                  data-testId={`${testId}-view-version-${version.version}`}
                />
              </div>
            </Tooltip>
            <div data-testId={`${testId}-download-version-${version.version}`}>
              <Tooltip message={t("global:download")} position={ToolTipPosition.top}>
                <BasicIcon 
                  clickable={true} 
                  onClick={() => downloadVersion(version.id, version.url || "", version.fileName || `terminos-y-condiciones-v${version.version}.pdf`)}
                  name="download" 
                  size="small" 
                  />
              </Tooltip>
            </div>
          </div>
        ),
      };
    });
  };

  return (
    <div className="terms-and-conditions-page">
      <div className="terms-header">
        <div className="terms-header-content">
          <h1 className="terms-title" data-testId={`${testId}-title`}>{t("termsAndConditions:title")}</h1>
          <p className="terms-subtitle" data-testId={`${testId}-subtitle`}>{t("termsAndConditions:subtitle")}</p>
        </div>
      </div>

      <div className="terms-content">
        <div className="terms-two-columns">
          <div className="terms-upload-card">
            <div className="terms-card-header">
              <h2 className="terms-section-title" data-testId={`${testId}-update-file-title`}>
                {t("termsAndConditions:updateFile")}
                <Tooltip message={t("termsAndConditions:tooltipInfo")} position={ToolTipPosition.bottomRight}>
                  <BasicIcon name="information-circle" size="extra-small" color="#0098ff" data-testId={`${testId}-tooltip-info`} />
                </Tooltip>
              </h2>
            </div>
            <div className="terms-upload-body">
              <PdfController
                ref={pdfControllerRef}
                processAllPages={true}
                title={t("termsAndConditions:fileLabel")}
                buttonText={t("termsAndConditions:loadFile")}
                fileUploaded={!!currentFile?.url}
                showError={fileStatus.error}
                errorMessage={fileStatus?.message || ""}
                loading={fileStatus.loading}
                fileHandler={fileHandler}
                disabled={fileStatus.loading}
                goToPreviousFile={goToPreviousFile}
                accept=".pdf"
                onFileTypeError={onFileTypeError}
              >
                {currentFile?.url && (
                  <PdfViewer src={currentFile.url}>
                    <Tooltip
                      disabled={!currentFile?.url}
                      message={t("global:delete")}
                      position={ToolTipPosition.top}
                    >
                      <button
                        data-testId={`${testId}-delete-pdf-button`}
                        className="icon-container-primary"
                        type="button"
                        disabled={!currentFile?.url}
                        onClick={onDeleteFile}
                      >
                        <BasicIcon name="trash" size="small" />
                      </button>
                    </Tooltip>
                    <div style={{ display: "none" }}>
                    <Tooltip
                      disabled={!currentFile?.url}
                      message={t("global:replace")}
                      position={ToolTipPosition.top}
                    >
                      <button
                        data-testId={`${testId}-replace-pdf-button`}
                        className="icon-container-primary"
                        type="button"
                        disabled={!currentFile?.url}
                        onClick={() => pdfControllerRef?.current?.triggerOnChange()}
                      >
                        <BasicIcon name="switch-vertical" size="small" />
                      </button>
                    </Tooltip>
                    </div>
                  </PdfViewer>
                )}
              </PdfController>
              <div className="terms-button-container">
                <Button
                  disabled={!currentFile?.url || fileStatus.loading}
                  testId={`${testId}-update-button`}
                  label={t("termsAndConditions:actualizar")}
                  onClick={onUpdate}
                />
              </div>
            </div>
          </div>

          <div className="terms-history-card">
            <div className="terms-card-header">
              <h2 className="terms-section-title" data-testId={`${testId}-version-history-title`}>{t("termsAndConditions:versionHistory")}</h2>
            </div>
            <div className="terms-history-body" data-testId={`${testId}-version-history-body`}>
              {loading && termsVersions.length === 0 ? (
                <div className="terms-loading-state">
                  <p>{t("termsAndConditions:loadingVersions") || "Cargando versiones..."}</p>
                </div>
              ) : termsVersions.length > 0 ? (
                <Table
                  headers={tableHeaders}
                  data={formatTableData()}
                  testId={`${testId}-versions-table`}
                />
              ) : (
                <div className="terms-empty-state">
                  <p>{t("termsAndConditions:noVersions")}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
